import React, { useState, useEffect } from 'react';
import { Settings, Save, Loader2, Share2, Phone, Instagram, Users, Mail } from 'lucide-react';
import api from '../../services/api';
import { Toast } from '../../Toast';

type Tab = 'geral' | 'redes';

// ─── Aba Geral ───────────────────────────────────────────────────────────────

const GeralTab: React.FC = () => {
    const [trialDays, setTrialDays] = useState(7);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    useEffect(() => {
        api.get('/banana/platform-settings')
            .then(res => setTrialDays(res.data.trial_days ?? 7))
            .catch(() => setToast({ msg: 'Erro ao carregar configurações', type: 'error' }))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.patch('/banana/platform-settings', { trial_days: trialDays });
            setToast({ msg: 'Configurações salvas com sucesso', type: 'success' });
        } catch {
            setToast({ msg: 'Erro ao salvar configurações', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-10 text-center text-gray-500">Carregando...</div>;

    return (
        <>
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                <h2 className="text-white font-bold text-sm mb-1">Período de Teste (Trial)</h2>
                <p className="text-gray-500 text-xs mb-4">Quantidade padrão de dias ao liberar trial manualmente para um usuário.</p>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min={0}
                            max={365}
                            value={trialDays}
                            onChange={e => setTrialDays(Math.min(365, Math.max(0, Number(e.target.value) || 0)))}
                            className="w-20 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-center text-sm focus:outline-none focus:ring-1 focus:ring-orange-500/50"
                        />
                        <span className="text-gray-400 text-sm">dias</span>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-2 rounded-xl bg-orange-600 hover:bg-orange-500 text-white text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-wait"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        Salvar
                    </button>
                </div>

                <div className="mt-4 p-3 bg-orange-500/5 border border-orange-500/10 rounded-xl">
                    <p className="text-xs text-gray-400">
                        {trialDays === 0
                            ? 'Trial desativado — nenhum período de teste será aplicado.'
                            : `Ao liberar trial, o usuário receberá ${trialDays} dia${trialDays > 1 ? 's' : ''} de acesso Pro.`}
                    </p>
                </div>
            </div>
        </>
    );
};

// ─── Aba Redes ───────────────────────────────────────────────────────────────

const RedesTab: React.FC = () => {
    const [whatsapp, setWhatsapp] = useState('');
    const [instagram, setInstagram] = useState('');
    const [networkingGroup, setNetworkingGroup] = useState('');
    const [supportEmail, setSupportEmail] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    useEffect(() => {
        api.get('/banana/social-settings')
            .then(res => {
                setWhatsapp(res.data.whatsapp_number || '');
                setInstagram(res.data.instagram_url || '');
                setNetworkingGroup(res.data.networking_group_url || '');
                setSupportEmail(res.data.support_email || '');
            })
            .catch(() => setToast({ msg: 'Erro ao carregar configurações de redes', type: 'error' }))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.patch('/banana/social-settings', {
                whatsapp_number: whatsapp,
                instagram_url: instagram,
                networking_group_url: networkingGroup,
                support_email: supportEmail,
            });
            setToast({ msg: 'Redes sociais salvas com sucesso', type: 'success' });
        } catch {
            setToast({ msg: 'Erro ao salvar', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-10 text-center text-gray-500">Carregando...</div>;

    return (
        <>
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            <div className="flex flex-col gap-5">
                {/* WhatsApp */}
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-1">
                        <Phone size={15} className="text-green-400" />
                        <h2 className="text-white font-bold text-sm">WhatsApp de Suporte</h2>
                    </div>
                    <p className="text-gray-500 text-xs mb-4">
                        Número usado no botão flutuante de suporte em todas as páginas. Apenas dígitos com código do país (ex: <span className="text-gray-300 font-mono">5511999999999</span>).
                    </p>
                    <input
                        type="text"
                        placeholder="5511999999999"
                        value={whatsapp}
                        onChange={e => setWhatsapp(e.target.value.replace(/\D/g, ''))}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-green-500/50 font-mono"
                    />
                </div>

                {/* Instagram */}
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-1">
                        <Instagram size={15} className="text-pink-400" />
                        <h2 className="text-white font-bold text-sm">Instagram</h2>
                    </div>
                    <p className="text-gray-500 text-xs mb-4">
                        URL do perfil no Instagram (ex: <span className="text-gray-300 font-mono">https://instagram.com/hotlize</span>).
                    </p>
                    <input
                        type="url"
                        placeholder="https://instagram.com/hotlize"
                        value={instagram}
                        onChange={e => setInstagram(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-pink-500/50"
                    />
                </div>

                {/* Email de Suporte */}
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-1">
                        <Mail size={15} className="text-orange-400" />
                        <h2 className="text-white font-bold text-sm">E-mail de Suporte</h2>
                    </div>
                    <p className="text-gray-500 text-xs mb-4">
                        Exibido na landing page como canal de contato clicável (ex: <span className="text-gray-300 font-mono">suporte@hotlize.com</span>).
                    </p>
                    <input
                        type="email"
                        placeholder="suporte@hotlize.com"
                        value={supportEmail}
                        onChange={e => setSupportEmail(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-orange-500/50"
                    />
                </div>

                {/* Grupo de Networking */}
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-1">
                        <Users size={15} className="text-blue-400" />
                        <h2 className="text-white font-bold text-sm">Grupo de Networking</h2>
                    </div>
                    <p className="text-gray-500 text-xs mb-4">
                        Link de convite do grupo (Telegram ou WhatsApp). Exibido na aba "Grupo de Networking" do painel dos usuários.
                    </p>
                    <input
                        type="url"
                        placeholder="https://t.me/seugrupo ou https://chat.whatsapp.com/..."
                        value={networkingGroup}
                        onChange={e => setNetworkingGroup(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                    />
                </div>

                <div className="flex justify-end">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-orange-600 hover:bg-orange-500 text-white text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-wait"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        Salvar Redes
                    </button>
                </div>
            </div>
        </>
    );
};

// ─── Página Principal ────────────────────────────────────────────────────────

export const PlatformSettings: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>('geral');

    const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
        { key: 'geral', label: 'Geral', icon: <Settings size={15} /> },
        { key: 'redes', label: 'Redes', icon: <Share2 size={15} /> },
    ];

    return (
        <div className="p-6 max-w-3xl">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Settings className="text-orange-500" size={24} /> Configurações da Plataforma
                </h1>
                <p className="text-gray-500 text-sm mt-1">Ajustes globais da plataforma</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 p-1 bg-white/[0.03] border border-white/10 rounded-xl w-fit">
                {tabs.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            activeTab === tab.key
                                ? 'bg-orange-600 text-white'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'geral' && <GeralTab />}
            {activeTab === 'redes' && <RedesTab />}
        </div>
    );
};
